import { c as _c } from "react/compiler-runtime";
import type { PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { useConfirmationDialog } from '@onefootprint/ui';
import { useQueryState } from 'nuqs';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'usehooks-ts';
import { useSetChatResourceListener } from '@/components/agent-chat/components/chat-widget/chat-resource-listener-context';
import type { ChatResourceEvent } from '@/components/agent-chat/hooks/use-chat-ws';
import { useDirtyStore } from '@/components/playbook-builder/hooks/use-dirty';
import { revealNodeErrors, usePlaybookBuilderStore } from '@/components/playbook-builder/hooks/use-store';
import useOnReturnToTab from '@/hooks/use-on-return-to-tab';
import usePlaybookId from '@/pages/playbooks/hooks/use-playbook-id';
import useAdoptEditSession from '../use-adopt-edit-session';
import { SELECTED_VERSION_QUERY_KEY } from '../use-playbook';
const usePercyPlaybookSync = playbookVersions => {
  const $ = _c(20);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "view"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t0);
  const playbookId = usePlaybookId();
  const adoptEditSession = useAdoptEditSession(playbookVersions);
  const setChatResourceListener = useSetChatResourceListener();
  const confirm = useConfirmationDialog();
  const [, setEditSessionIdParam] = useQueryState("editSessionId");
  const [, setVersionParam] = useQueryState(SELECTED_VERSION_QUERY_KEY);
  const pendingSyncRef = useRef(null);
  const promptedSessionRef = useRef(null);
  let t1;
  if ($[1] !== adoptEditSession) {
    t1 = (editSessionId, t2) => {
      const {
        reveal: t3
      } = t2 === undefined ? {} : t2;
      const reveal = t3 === undefined ? false : t3;
      if (useDirtyStore.getState().data.dirtyComponents.length > 0) {
        const pending = pendingSyncRef.current;
        pendingSyncRef.current = {
          editSessionId,
          reveal: reveal || pending?.editSessionId === editSessionId && pending.reveal
        };
        return;
      }
      const adopt = () => adoptEditSession(editSessionId, {
        silent: true,
        onlyIfCurrent: true
      });
      adopt().then(result => result === "stale" ? adopt() : result).then(result_0 => {
        if (reveal && result_0 && result_0 !== "stale") {
          revealNodeErrors();
        }
      });
    };
    $[1] = adoptEditSession;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const syncSession = useEventCallback(t1);
  let t2;
  if ($[3] !== syncSession) {
    t2 = () => {
      const {
        sessionId
      } = usePlaybookBuilderStore.getState().data;
      if (sessionId) {
        syncSession(sessionId);
      }
    };
    $[3] = syncSession;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const refetchOpenSession = useEventCallback(t2);
  let t3;
  if ($[5] !== confirm || $[6] !== playbookId || $[7] !== setEditSessionIdParam || $[8] !== setVersionParam || $[9] !== syncSession || $[10] !== t) {
    t3 = event => {
      if (event.resource.kind !== "playbook") {
        return;
      }
      const {
        id,
        edit_session_id: editSessionId_0,
        version_id: versionId
      } = event.resource;
      if (!editSessionId_0 || id !== playbookId) {
        return;
      }
      const {
        sessionId: sessionId_0
      } = usePlaybookBuilderStore.getState().data;
      if (editSessionId_0 === sessionId_0) {
        syncSession(editSessionId_0, {
          reveal: true
        });
        return;
      }
      const switchToPercyDraft = () => {
        if (versionId) {
          setVersionParam(versionId);
        }
        setEditSessionIdParam(editSessionId_0);
      };
      if (!sessionId_0) {
        switchToPercyDraft();
        return;
      }
      if (promptedSessionRef.current === editSessionId_0) {
        return;
      }
      promptedSessionRef.current = editSessionId_0;
      confirm.open({
        title: t(_temp),
        description: t(_temp2),
        variant: "default",
        primaryButton: {
          label: t(_temp3),
          onClick: switchToPercyDraft
        },
        secondaryButton: {
          label: t(_temp4)
        }
      });
    };
    $[5] = confirm;
    $[6] = playbookId;
    $[7] = setEditSessionIdParam;
    $[8] = setVersionParam;
    $[9] = syncSession;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const onResourceEvent = useEventCallback(t3);
  useOnReturnToTab(refetchOpenSession);
  let t4;
  let t5;
  if ($[12] !== syncSession) {
    t4 = () => useDirtyStore.subscribe(state => {
      if (state.data.dirtyComponents.length > 0 || !pendingSyncRef.current) {
        return;
      }
      const pending_0 = pendingSyncRef.current;
      pendingSyncRef.current = null;
      syncSession(pending_0.editSessionId, {
        reveal: pending_0.reveal
      });
    });
    t5 = [syncSession];
    $[12] = syncSession;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[15] !== onResourceEvent || $[16] !== refetchOpenSession || $[17] !== setChatResourceListener) {
    t6 = () => {
      setChatResourceListener({
        onResourceEvent,
        onReady: refetchOpenSession
      });
      return () => setChatResourceListener(null);
    };
    t7 = [setChatResourceListener, onResourceEvent, refetchOpenSession];
    $[15] = onResourceEvent;
    $[16] = refetchOpenSession;
    $[17] = setChatResourceListener;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
};
export default usePercyPlaybookSync;
function _temp($) {
  return $.percyDraftSwitch.title;
}
function _temp2($_0) {
  return $_0.percyDraftSwitch.description;
}
function _temp3($_1) {
  return $_1.percyDraftSwitch.confirm;
}
function _temp4($_2) {
  return $_2.percyDraftSwitch.cancel;
}